import { StyleSheet, View } from "react-native";
import { DayOfWeek, WORKING_DAYS_PER_WEEK, minutesAsString } from "../../utils";
import React, { useCallback, useMemo, useState } from "react";
import ChildrenGroup from "../ChildrenGroup";
import Switch from "../../ui_atoms/Switch";
import { useDurationFormatter, useI18n } from "../../hooks/i18n-hooks";
import { useTheme } from "../../hooks/use-theme";
import TimeField from "../TimeField";
import {
    DayScheduleType,
    TimeMode,
    ScheduleType,
    WorkingTimeScheduleType,
    ScheduleTimeType,
} from "../../__generated__/graphql";
import TimeListComponent, {
    verifyScheduleTypesFixedTimes,
    verifyScheduleTypesFreeDuration,
} from "./TimeListComponent";
import { Buildable } from "ts-essentials";
import VStack from "../../ui_atoms/VStack";
import OptionsField from "../OptionsField";
import { OptionType } from "../OptionsSelector";
import Gap from "../../ui_atoms/Gap";
import InfoText from "../InfoText";
import Text from "../Text";

/**
 * Component to handle WorkingTimeScheduleType
 */
export function useWeekDayNames() {
    const i18n = useI18n();
    return useCallback(
        (dayOfWeek: DayOfWeek) => i18n.t(`utils.${dayOfWeek.toLowerCase()}`),
        [i18n],
    );
}

const TimeAsMinuteInput: React.FC<{
    value: number;
    onChange: (value: number) => void;
    disabled?: boolean;
    day: DayOfWeek;
}> = ({ value, onChange, disabled = false, day }) => {
    return (
        <TimeField
            setTimeFn={day}
            value={value}
            onChange={onChange}
            isDuration
            minuteInterval={1}
            disabled={disabled}
            style={{ flexGrow: 1, flexShrink: 1 }}
        />
    );
};

const getDefaultDaySchedule = (timeMode: TimeMode, hoursPerWeek: number): DayScheduleType => {
    if (timeMode == TimeMode.Fixed) {
        return {
            timeMode: TimeMode.Fixed,
            fixedTimes: [
                {
                    startTime: 8 * 60,
                    endTime: 12 * 60,
                    type: ScheduleTimeType.Work,
                },
                {
                    startTime: 13 * 60,
                    endTime: 17 * 60 + 24,
                    type: ScheduleTimeType.Work,
                },
            ],
        };
    } else if (timeMode == TimeMode.Free) {
        return {
            timeMode: TimeMode.Free,
            freeDurations: [
                {
                    duration: (hoursPerWeek * 60) / WORKING_DAYS_PER_WEEK,
                    type: ScheduleTimeType.Work,
                },
            ],
        };
    }
    throw new Error(`Unknown timeModes`);
};

type WeekDayCellProps = {
    day: DayOfWeek;
    daySchedule: DayScheduleType;
    onChange: (day: DayOfWeek, daySchedule: DayScheduleType) => void;
    enabled: boolean;
    onChangeEnabled: (day: DayOfWeek, enabled: boolean) => void;
    disabled?: boolean; // this is to disable the whole cell ( the switch also )
};

const WeekDayCell: React.FC<WeekDayCellProps> = ({
    day,
    daySchedule,
    onChange,
    enabled,
    onChangeEnabled,
    disabled = false,
}) => {
    const i18n = useI18n();
    const componentStyle = useWeekDayCellStyle();

    const getWeekDayName = useWeekDayNames();

    const onChangeFreeDuration = useCallback(
        (freeDurations: number) => {
            onChange(day, {
                ...daySchedule,
                freeDurations: [
                    {
                        duration: freeDurations,
                        type: ScheduleTimeType.Work,
                    },
                ],
            });
        },
        [onChange, daySchedule, day],
    );

    const onChangeTimes = useCallback(
        (times: ScheduleType[]) => {
            onChange(day, {
                ...daySchedule,
                fixedTimes: times,
            });
        },
        [onChange, daySchedule, day],
    );

    const duration = useMemo(() => {
        const minutes = daySchedule?.fixedTimes?.reduce((acc, time) => {
            return acc + (time.endTime - time.startTime);
        }, 0);

        return minutes ? minutesAsString(minutes) : undefined;
    }, [daySchedule?.fixedTimes]);

    const errors = useMemo(() => {
        return verifyDaySchedule([daySchedule]);
    }, [daySchedule]);

    return (
        <View style={componentStyle.cell}>
            <View style={componentStyle.cellHeader}>
                <Switch
                    value={enabled}
                    onChange={(en) => onChangeEnabled(day, en)}
                    style={componentStyle.switchStyle}
                    disabled={disabled}
                    testID={`weekday-switch-${day}`}
                />
                <Text style={componentStyle.cellHeaderTitle}>{getWeekDayName(day)}</Text>
                {enabled && daySchedule?.timeMode === TimeMode.Fixed && duration && (
                    <Text style={componentStyle.headerBadge}>{duration}</Text>
                )}
            </View>
            {errors.map((err) => (
                <Text key={err} style={componentStyle.errorText}>
                    {i18n.t(err)}
                </Text>
            ))}
            {enabled &&
                daySchedule &&
                daySchedule.timeMode == TimeMode.Free &&
                daySchedule.freeDurations && (
                    <View style={componentStyle.modeFree}>
                        <Text style={componentStyle.durationLabel}>{i18n.t("utils.duration")}</Text>
                        <TimeAsMinuteInput
                            value={daySchedule.freeDurations[0].duration || 0}
                            onChange={onChangeFreeDuration}
                            disabled={disabled}
                            day={day}
                        />
                    </View>
                )}
            {enabled && daySchedule && daySchedule.timeMode == TimeMode.Fixed && (
                <TimeListComponent
                    times={daySchedule.fixedTimes || []}
                    onChange={onChangeTimes}
                    disabled={disabled}
                    day={day}
                />
            )}
        </View>
    );
};

const useWeekDayCellStyle = () => {
    const { gridSize, colors, fonts, marginSize } = useTheme();
    return useMemo(
        () =>
            StyleSheet.create({
                cell: {
                    paddingVertical: 5,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "flex-start",
                },
                cellHeader: {
                    display: "flex",
                    flexDirection: "row",
                    alignItems: "center",
                    width: "100%",
                    paddingHorizontal: marginSize,
                },
                headerBadge: {
                    flex: 0,
                    textTransform: "capitalize",
                    ...fonts.bodySmall,
                    flexGrow: 1,
                    flexShrink: 0,
                    textAlign: "right",
                },
                switchStyle: {
                    paddingVertical: gridSize / 2,
                },
                cellHeaderTitle: {
                    fontWeight: "bold",
                    flexGrow: 1,
                    fontSize: 16,
                },
                modeFree: {
                    display: "flex",
                    flexDirection: "row",
                    alignItems: "center",
                    paddingHorizontal: marginSize,
                    width: "100%",
                },
                durationLabel: {
                    paddingRight: gridSize / 2,
                },
                errorText: {
                    color: colors.error,
                    alignSelf: "center",
                    marginBottom: gridSize / 2,
                },
            }),
        [marginSize, fonts.bodySmall, gridSize, colors.error],
    );
};

function findTimeMode(workingTime: WorkingTimeScheduleType): TimeMode {
    const daySchedule = Object.values(workingTime).find((daySchedule) => !!daySchedule);
    return daySchedule ? daySchedule.timeMode : TimeMode.Free;
}

export type WorkingTimeScheduledWeekComponentProps = {
    workingTime: WorkingTimeScheduleType;
    onChange: (workingTime: WorkingTimeScheduleType) => void;
    disabled?: boolean;
    hoursPerWeek: number;
};

const WorkingTimeScheduleWeekComponent: React.FC<WorkingTimeScheduledWeekComponentProps> = ({
    workingTime,
    onChange,
    disabled = false,
    hoursPerWeek,
}) => {
    const i18n = useI18n();

    const [timeMode, setTimeMode] = useState<TimeMode>(findTimeMode(workingTime));

    const componentStyle = useWorkingTimeScheduleWeekStyle();
    const onChangeDaySchedule = useCallback(
        (day: DayOfWeek, daySchedule: DayScheduleType | null) => {
            if (daySchedule) {
                onChange({
                    ...workingTime,
                    [day]: { ...workingTime[day], ...daySchedule },
                });
            } else {
                const { [day]: existingDay, ...newWorkingTime } = workingTime || {};
                onChange({
                    ...newWorkingTime,
                    [day]: {
                        ...existingDay,
                        ...(timeMode === TimeMode.Fixed ? { fixedTimes: undefined } : {}),
                        ...(timeMode === TimeMode.Free ? { freeDurations: undefined } : {}),
                    },
                });
            }
        },
        [onChange, timeMode, workingTime],
    );

    const onTimeModeChange = useCallback(
        (mode: TimeMode) => {
            // replace all timeMode of the workingTime with the new one
            const newWorkingTime = Object.values(DayOfWeek).reduce((acc, day) => {
                const wd = workingTime[day];
                if (wd) {
                    acc[day] = {
                        fixedTimes: wd.fixedTimes,
                        freeDurations: wd.freeDurations,
                        timeMode: mode,
                    };
                }
                return acc;
            }, {} as WorkingTimeScheduleType);
            setTimeMode(mode);
            onChange(newWorkingTime);
        },
        [onChange, workingTime],
    );

    const timeModeData = useMemo(
        () =>
            [TimeMode.Free, TimeMode.Fixed].map((value) => ({
                label: i18n.t(`utils.working_time.schedule.modes.${value}`),
                value: value,
            })) as [OptionType<TimeMode>, OptionType<TimeMode>],
        [i18n],
    );
    const formatDuration = useDurationFormatter();
    const totalDurationInfo = useMemo(() => {
        const duration = Object.values(workingTime).reduce((acc, daySchedule) => {
            if (daySchedule?.timeMode == TimeMode.Free) {
                if (daySchedule.freeDurations) {
                    return acc + (daySchedule.freeDurations[0].duration || 0);
                }
                return acc;
            }
            if (daySchedule?.timeMode == TimeMode.Fixed) {
                return (
                    acc +
                    (daySchedule.fixedTimes?.reduce((acc, time) => {
                        return acc + (time.endTime - time.startTime);
                    }, 0) || 0)
                );
            }
            return acc;
        }, 0);
        if (duration > 0) {
            return i18n.t("utils.working_time.schedule.duration_per_week", {
                duration: formatDuration(duration),
            });
        }
        return i18n.t("utils.working_time.schedule.no_day_selected");
    }, [formatDuration, i18n, workingTime]);

    return (
        <VStack gap={0} h={0}>
            <VStack h="2/3">
                <OptionsField value={timeMode} onSelect={onTimeModeChange} data={timeModeData} />
            </VStack>
            {totalDurationInfo && (
                <VStack h="2/3">
                    <Gap gap={1} />
                    <InfoText text={totalDurationInfo} />
                </VStack>
            )}
            <ChildrenGroup style={componentStyle.container}>
                {Object.values(DayOfWeek).map((day) => (
                    <WeekDayCell
                        day={day}
                        key={day}
                        daySchedule={workingTime[day] as DayScheduleType}
                        onChange={onChangeDaySchedule}
                        enabled={
                            (timeMode === TimeMode.Free && !!workingTime[day]?.freeDurations) ||
                            (timeMode === TimeMode.Fixed && !!workingTime[day]?.fixedTimes)
                        }
                        onChangeEnabled={(day, enabled) => {
                            if (enabled) {
                                // take previous value and set it as current value
                                // if not exist, use the default
                                onChangeDaySchedule(
                                    day,
                                    getDefaultDaySchedule(timeMode, hoursPerWeek),
                                );
                            } else {
                                onChangeDaySchedule(day, null);
                            }
                        }}
                        disabled={disabled}
                    />
                ))}
            </ChildrenGroup>
            {/* this a trick when last WeekDayCell open, to show to the user
            that something has changed and he can scroll the view */}
            <Gap gap={2} />
        </VStack>
    );
};

const useWorkingTimeScheduleWeekStyle = () => {
    const { colors } = useTheme();
    return useMemo(
        () =>
            StyleSheet.create({
                container: {
                    borderWidth: 0,
                    borderRadius: 0,
                    borderColor: colors.backdrop,
                },
            }),
        [colors],
    );
};
export default WorkingTimeScheduleWeekComponent;

export const verifyWorkingTimeScheduleType = (
    value?: Buildable<WorkingTimeScheduleType> | null,
): string[] => {
    if (!value) {
        return [];
    }
    return verifyDaySchedule(Object.values(value));
};

export const verifyDaySchedule = (
    daySchedules?: Array<Buildable<DayScheduleType> | undefined | null> | null,
): string[] => {
    return Array.from(
        (daySchedules || []).reduce((acc, daySchedule) => {
            const errors = [];
            if (daySchedule) {
                if (daySchedule.timeMode == TimeMode.Fixed && daySchedule.fixedTimes) {
                    errors.push(...verifyScheduleTypesFixedTimes(daySchedule.fixedTimes));
                }
                if (daySchedule.timeMode == TimeMode.Free && daySchedule.freeDurations) {
                    errors.push(...verifyScheduleTypesFreeDuration(daySchedule.freeDurations));
                }
            }
            return new Set([...acc, ...errors]);
        }, new Set<string>()),
    );
};
